const DEFAULT_NEXT = '/dashboard';

/**
 * 401 で飛ばされてきたときの戻り先（api.js / AuthGuard が付ける ?next=）を解決する。
 *
 * オープンリダイレクトを防ぐため、**同一オリジンに解決されるものだけ**を通す。
 * 文字列の前方一致だけでは不十分で、URLパーサはタブ・改行・復帰を
 * 除去してから解釈するため、たとえば "/\t/evil.example" は "//evil.example"
 * （＝プロトコル相対の外部URL）になる。実際にURLとして解決してオリジンを
 * 突き合わせるのが確実。
 *
 * @param {string} search location.search（"?next=..."）
 * @param {string} [origin] 判定の基準にするオリジン（既定は window.location.origin）
 * @returns {string} 同一オリジンのパス。判定できないものは /dashboard
 */
export function safeNext(search, origin) {
  let next;
  try {
    next = new URLSearchParams(search).get('next');
  } catch {
    return DEFAULT_NEXT;
  }
  if (!next) return DEFAULT_NEXT;

  // 絶対パス以外は受け付けない（相対パスを許すと意図が読みにくくなるだけで利点が無い）
  if (!next.startsWith('/')) return DEFAULT_NEXT;

  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : null);
  if (!base) return DEFAULT_NEXT;

  let url;
  let baseUrl;
  try {
    baseUrl = new URL(base);
    url = new URL(next, baseUrl);
  } catch {
    return DEFAULT_NEXT;
  }
  // "javascript:" 等は origin が "null" になるのでここで落ちる
  if (url.origin !== baseUrl.origin) return DEFAULT_NEXT;

  const path = url.pathname + url.search + url.hash;
  // 正規化の結果が "//..." になることがある（例: "/..//evil.example"）。
  // 同一オリジンのパスではあるが、この文字列がそのまま location.assign() に
  // 渡るとプロトコル相対URLとして外部に飛ぶ。出口でも弾いておく。
  if (!/^\/(?![/\\])/.test(path)) return DEFAULT_NEXT;

  return path;
}
